/**
 * @file DashboardController.jsx
 * @description Controls dashboard data flow and state management
 *
 * @section Responsibilities
 * - Control dashboard data flow
 * - Manage dashboard state
 * - Process dashboard updates
 * - Handle dashboard errors
 * - Provide dashboard controls
 * - Manage dashboard interactions
 *
 * @section Dependencies
 * - DashboardService: Dashboard management
 * - DataService: Data processing
 * - StateService: State management
 * - ControlService: Control handling
 *
 * @section Integration Points
 * - Dashboard API: Data operations
 * - Data System: Data processing
 * - State System: State management
 * - Control System: User interactions
 *
 * @section State Management
 * - controlState: Control status
 * - dataState: Data status
 * - updateState: Update status
 * - errorState: Error handling
 *
 * @section Events
 * - onControl: Control handling
 * - onDataProcess: Data processing
 * - onStateChange: State updates
 * - onError: Error handling
 *
 * @section Props
 * @prop {Object} controlConfig - Control configuration
 * @prop {Function} onComplete - Completion callback
 * @prop {Object} initialState - Initial control state
 * @prop {Boolean} autoControl - Controls auto-controls
 */

import React, { useState, useEffect } from "react";
import ClientDashboard from "./modules/clients/ClientDashboard";
import AdminDashboard from "./modules/admins/AdminDashboard";
import FundManagerDashboard from "./modules/fundManagers/FundManagerDashboard";
import AdvisorDashboard from "./modules/advisors/AdvisorDashboard";
import TwoFactorPrompt from "../Authentication/TwoFactorPrompt";
import { TimeFilterProvider } from "./../../contexts/TimeFilterContext";

function DashboardController() {
  var user = JSON.parse(localStorage.session);
  const role = user.role;
  const [showTwoFactorPrompt, setShowTwoFactorPrompt] = useState(false);
  useEffect(() => {
    const checkTwoFactorRequired = () => {
      if (user && user.email) {
        if (!user.twoFactorEnabled && role !== "customer") {
          setShowTwoFactorPrompt(true);
        }
      }
    };

    checkTwoFactorRequired();
  }, [user, role]);

  const handleTwoFactorComplete = () => {
    setShowTwoFactorPrompt(false);
    const updatedUser = { ...user, twoFactorEnabled: true };
    localStorage.setItem('session', JSON.stringify(updatedUser));
  };
  const handleTwoFactorSkip = () => {
    setShowTwoFactorPrompt(false);
  };

  if (showTwoFactorPrompt) {
    return (
      <TwoFactorPrompt
        open={true}
        onComplete={handleTwoFactorComplete}
        onSkip={handleTwoFactorSkip}
        mandatory={role !== "customer"}
      />
    );
  }

  return (
    <>
      <TimeFilterProvider>
        {role === "customer" ? (
          <ClientDashboard />
        ) : role === "admin" ? (
          <AdminDashboard />
        ) : role === "fundManager" ? (
          <FundManagerDashboard />
        ) : role === "advisor" ? (
          <AdvisorDashboard />
        ) : null}
      </TimeFilterProvider>
    </>
  );
}

export default DashboardController;
